// Grammar Units 11 Data - Unit 11: Conjunctions Complete Learning Course
// Based on Ch11 (Part 1).jsx, Ch11 (Part 2).jsx, and Ch11 (Part 3).jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits11Data = [
  {
    id: 11,
    title: { traditional: '連接詞完全學習課程', simplified: '连接词完全学习课程', english: 'Conjunctions Complete Learning Course' },
    subtitle: { traditional: '掌握各種連接詞的用法', simplified: '掌握各种连接词的用法', english: 'Master various uses of conjunctions' },
    description: { traditional: '系統性學習英文連接詞，包括對等連接詞、從屬連接詞、相關連接詞和連接副詞。', simplified: '系统性学习英文连接词，包括对等连接词、从属连接词、相关连接词和连接副词。', english: 'Systematic learning of English conjunctions including coordinating conjunctions, subordinating conjunctions, correlative conjunctions and conjunctive adverbs.' },
    icon: '🔗',
    color: 'from-indigo-500 to-purple-600',
    status: 'notStarted',
    difficulty: 'intermediate',
    estimatedHours: 4,
    content: {
      overview: {
        title: "連接詞概覽",
        description: "連接詞是用來連接單字、片語、子句或句子的詞類，主要分為對等連接詞、從屬連接詞、相關連接詞和連接副詞。",
        types: [
          { type: "對等連接詞", examples: "and, or, but, so, yet, for, nor", function: "連接同等地位的句子成分" },
          { type: "從屬連接詞", examples: "because, when, if, although, since", function: "連接主要子句和從屬子句" },
          { type: "相關連接詞", examples: "both...and, either...or, neither...nor", function: "成對出現的連接詞" },
          { type: "連接副詞", examples: "however, therefore, moreover, furthermore", function: "連接句子或段落，表示邏輯關係" }
        ]
      },
      coordinating_conjunctions: {
        title: "對等連接詞 (Coordinating Conjunctions)",
        description: "對等連接詞用來連接同等地位的句子成分，包括單字、片語或子句。",
        conjunctions: [
          {
            word: "and",
            meaning: "和、並且",
            usage: "連接相似或相關的概念",
            examples: [
              "I like coffee and tea.",
              "She is smart and beautiful.",
              "He studied hard and passed the exam."
            ]
          },
          {
            word: "or",
            meaning: "或者",
            usage: "表示選擇或替代",
            examples: [
              "Would you like coffee or tea?",
              "You can study now or later.",
              "Either you come with us or stay here."
            ]
          },
          {
            word: "but",
            meaning: "但是",
            usage: "表示對比或轉折",
            examples: [
              "I like coffee, but I don't like tea.",
              "She is smart, but she is not arrogant.",
              "He tried hard, but he failed."
            ]
          },
          {
            word: "so",
            meaning: "所以",
            usage: "表示結果或目的",
            examples: [
              "It was raining, so I took an umbrella.",
              "I was tired, so I went to bed early.",
              "She studied hard, so she passed the exam."
            ]
          },
          {
            word: "yet",
            meaning: "然而、但是",
            usage: "表示對比或讓步",
            examples: [
              "He is rich, yet he lives simply.",
              "She is young, yet she is very wise.",
              "It was late, yet he continued working."
            ]
          },
          {
            word: "for",
            meaning: "因為",
            usage: "表示原因或理由",
            examples: [
              "I stayed home, for it was raining.",
              "She didn't come, for she was sick.",
              "He left early, for he had an appointment."
            ]
          },
          {
            word: "nor",
            meaning: "也不",
            usage: "表示否定選擇",
            examples: [
              "I don't like coffee, nor do I like tea.",
              "She didn't call, nor did she write.",
              "He can't swim, nor can he ride a bike."
            ]
          }
        ],
        memory_tip: "記憶口訣：FANBOYS (For, And, Nor, But, Or, Yet, So)"
      },
      correlative_conjunctions: {
        title: "相關連接詞 (Correlative Conjunctions)",
        description: "相關連接詞是成對出現的連接詞，用來連接同等地位的句子成分。",
        pairs: [
          {
            pair: "both...and",
            meaning: "既...又...",
            usage: "表示兩個都",
            examples: [
              "Both Tom and Jerry are students.",
              "She is both smart and beautiful.",
              "He both studies and works."
            ]
          },
          {
            pair: "either...or",
            meaning: "或者...或者...",
            usage: "表示選擇",
            examples: [
              "Either you or I will go.",
              "You can either study or work.",
              "Either he comes or I leave."
            ]
          },
          {
            pair: "neither...nor",
            meaning: "既不...也不...",
            usage: "表示否定選擇",
            examples: [
              "Neither Tom nor Jerry came.",
              "She neither called nor wrote.",
              "He neither studies nor works."
            ]
          },
          {
            pair: "not only...but also",
            meaning: "不僅...而且...",
            usage: "表示強調",
            examples: [
              "Not only is he smart, but also he is hardworking.",
              "She not only sings, but also dances.",
              "Not only did he pass, but also he got the highest score."
            ]
          },
          {
            pair: "whether...or",
            meaning: "是否...或者...",
            usage: "表示選擇或疑問",
            examples: [
              "I don't know whether to go or stay.",
              "Whether you like it or not, you have to do it.",
              "Whether he comes or not, the party will go on."
            ]
          }
        ]
      },
      subordinating_conjunctions: {
        title: "從屬連接詞 (Subordinating Conjunctions)",
        description: "從屬連接詞用來連接主要子句和從屬子句，表示時間、原因、條件、讓步等關係。",
        categories: [
          {
            category: "時間",
            conjunctions: [
              {
                word: "when",
                meaning: "當...時",
                examples: [
                  "When I arrived, the party had already started.",
                  "I was reading when the phone rang.",
                  "When you finish, let me know."
                ]
              },
              {
                word: "while",
                meaning: "當...時、在...期間",
                examples: [
                  "While I was studying, my brother was playing.",
                  "I met her while I was traveling.",
                  "While you're here, could you help me?"
                ]
              },
              {
                word: "before",
                meaning: "在...之前",
                examples: [
                  "I'll finish this before I go.",
                  "Before you leave, please close the door.",
                  "I had never seen her before I came here."
                ]
              },
              {
                word: "after",
                meaning: "在...之後",
                examples: [
                  "After I finish work, I'll go home.",
                  "After you eat, you can watch TV.",
                  "I'll call you after I arrive."
                ]
              },
              {
                word: "since",
                meaning: "自從...以來",
                examples: [
                  "I haven't seen her since she left.",
                  "Since I started working, I've been very busy.",
                  "It's been years since we last met."
                ]
              },
              {
                word: "until",
                meaning: "直到...為止",
                examples: [
                  "I'll wait until you come back.",
                  "Don't leave until I return.",
                  "I worked until midnight."
                ]
              }
            ]
          },
          {
            category: "原因",
            conjunctions: [
              {
                word: "because",
                meaning: "因為",
                examples: [
                  "I stayed home because it was raining.",
                  "She didn't come because she was sick.",
                  "He left early because he had an appointment."
                ]
              },
              {
                word: "since",
                meaning: "既然、因為",
                examples: [
                  "Since you're here, let's start.",
                  "Since it's late, we should go home.",
                  "Since you know the answer, tell me."
                ]
              },
              {
                word: "as",
                meaning: "因為、由於",
                examples: [
                  "As it was raining, I took an umbrella.",
                  "As you're tired, you should rest.",
                  "As he was late, he missed the train."
                ]
              }
            ]
          },
          {
            category: "條件",
            conjunctions: [
              {
                word: "if",
                meaning: "如果",
                examples: [
                  "If it rains, I'll stay home.",
                  "If you study hard, you'll pass.",
                  "If he comes, tell him to wait."
                ]
              },
              {
                word: "unless",
                meaning: "除非",
                examples: [
                  "I won't go unless you come with me.",
                  "Unless you hurry, you'll be late.",
                  "I won't tell anyone unless you want me to."
                ]
              },
              {
                word: "provided that",
                meaning: "假設、如果",
                examples: [
                  "I'll help you provided that you promise to study.",
                  "You can use my car provided that you drive carefully.",
                  "I'll come provided that I'm not busy."
                ]
              }
            ]
          },
          {
            category: "讓步",
            conjunctions: [
              {
                word: "although",
                meaning: "雖然",
                examples: [
                  "Although it was raining, I went out.",
                  "Although he is rich, he lives simply.",
                  "Although she is young, she is very wise."
                ]
              },
              {
                word: "though",
                meaning: "雖然",
                examples: [
                  "Though it was late, he continued working.",
                  "Though he tried hard, he failed.",
                  "Though she was tired, she kept going."
                ]
              },
              {
                word: "even though",
                meaning: "即使",
                examples: [
                  "Even though it was expensive, I bought it.",
                  "Even though he was sick, he went to work.",
                  "Even though she knew the answer, she didn't speak."
                ]
              }
            ]
          }
        ]
      },
      conjunctive_adverbs: {
        title: "連接副詞 (Conjunctive Adverbs)",
        description: "連接副詞用來連接句子或段落，表示邏輯關係，通常用分號或句號與前面的句子分開。",
        categories: [
          {
            category: "對比",
            adverbs: [
              {
                word: "however",
                meaning: "然而",
                examples: [
                  "I like coffee; however, I don't drink it often.",
                  "He is smart. However, he is not arrogant.",
                  "The weather was bad. However, we still went hiking."
                ]
              },
              {
                word: "nevertheless",
                meaning: "然而、不過",
                examples: [
                  "It was raining; nevertheless, we went out.",
                  "He was tired. Nevertheless, he continued working.",
                  "The food was expensive. Nevertheless, it was delicious."
                ]
              },
              {
                word: "on the other hand",
                meaning: "另一方面",
                examples: [
                  "I like coffee. On the other hand, my sister prefers tea.",
                  "He is good at math. On the other hand, he struggles with English.",
                  "The movie was long. On the other hand, it was very interesting."
                ]
              }
            ]
          },
          {
            category: "結果",
            adverbs: [
              {
                word: "therefore",
                meaning: "因此",
                examples: [
                  "He studied hard; therefore, he passed the exam.",
                  "It was raining. Therefore, I took an umbrella.",
                  "She was sick. Therefore, she didn't come to the party."
                ]
              },
              {
                word: "thus",
                meaning: "因此、這樣",
                examples: [
                  "He didn't study; thus, he failed the exam.",
                  "The weather was bad. Thus, we stayed home.",
                  "She was busy. Thus, she couldn't help us."
                ]
              },
              {
                word: "consequently",
                meaning: "結果",
                examples: [
                  "He was late; consequently, he missed the train.",
                  "She forgot her keys. Consequently, she couldn't enter the house.",
                  "The road was closed. Consequently, we had to take a detour."
                ]
              }
            ]
          },
          {
            category: "補充",
            adverbs: [
              {
                word: "moreover",
                meaning: "此外",
                examples: [
                  "He is smart; moreover, he is hardworking.",
                  "The food was delicious. Moreover, it was healthy.",
                  "She is beautiful. Moreover, she is kind."
                ]
              },
              {
                word: "furthermore",
                meaning: "此外",
                examples: [
                  "He is qualified; furthermore, he has experience.",
                  "The price is reasonable. Furthermore, the quality is good.",
                  "She is talented. Furthermore, she is dedicated."
                ]
              },
              {
                word: "in addition",
                meaning: "此外",
                examples: [
                  "He speaks English; in addition, he speaks French.",
                  "The hotel is clean. In addition, it is comfortable.",
                  "She is a good teacher. In addition, she is a good friend."
                ]
              }
            ]
          },
          {
            category: "時間",
            adverbs: [
              {
                word: "meanwhile",
                meaning: "同時",
                examples: [
                  "I was cooking; meanwhile, my husband was cleaning.",
                  "She was studying. Meanwhile, her friends were playing.",
                  "He was working. Meanwhile, his wife was shopping."
                ]
              },
              {
                word: "subsequently",
                meaning: "隨後",
                examples: [
                  "He graduated; subsequently, he found a job.",
                  "She moved to a new city. Subsequently, she made new friends.",
                  "He lost his job. Subsequently, he started his own business."
                ]
              }
            ]
          }
        ]
      },
      exercises: {
        title: "練習題",
        sections: [
          {
            name: "連接詞練習 1",
            questions: [
              {
                question: "1. I like coffee __________ tea.",
                options: {
                  A: "and",
                  B: "or", 
                  C: "but",
                  D: "so"
                },
                answer: "A",
                explanation: "and 用來連接相似的概念"
              },
              {
                question: "2. It was raining, __________ I took an umbrella.",
                options: {
                  A: "and",
                  B: "or",
                  C: "but",
                  D: "so"
                },
                answer: "D",
                explanation: "so 表示結果"
              }
            ]
          },
          {
            name: "連接詞練習 2",
            questions: [
              {
                question: "1. __________ you study hard, you will pass the exam.",
                options: {
                  A: "If",
                  B: "When",
                  C: "Because",
                  D: "Although"
                },
                answer: "A",
                explanation: "if 表示條件"
              },
              {
                question: "2. __________ it was expensive, I bought it.",
                options: {
                  A: "If",
                  B: "When",
                  C: "Because",
                  D: "Although"
                },
                answer: "D",
                explanation: "although 表示讓步"
              }
            ]
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits11Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits11Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits11Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits11Data;
